<template>
	<div v-if="availableLanguages.length > 1" class="nav-language-switch" ref="languageSwitchRef">
		<el-popover
			placement="bottom-start"
			trigger="hover"
			popper-class="nav-language-popover"
			:append-to-body="false"
			:popper-options="{
				boundariesElement: 'viewport',
				preventOverflow: true
			}"
			:visible-arrow="false"
			:offset="-16"
			:reference="languageSwitchRef"
		>
			<template #reference>
				<div class="nav-language-switch-trigger">
					<div class="nav-language-switch-item">
						{{ currentLanguageLabel }}
					</div>
					<SvgIcons name="down" class="nav-language-switch-arrow" />
				</div>
			</template>

			<!-- 语言下拉列表 -->
			<div class="nav-language-dropdown">
				<div
					v-for="lang in dropdownLanguages"
					:key="lang.key"
					class="nav-language-dropdown-item"
					@click="handleLangChange(lang.key)"
				>
					<div class="nav-language-dropdown-item-content">
						{{ lang.value }}
					</div>
				</div>
			</div>
		</el-popover>
	</div>
</template>

<script setup>
import { useLanguageSwitch } from '@rebuild/materials/shared/floating/hooks/useLanguageSwitch.js';
import i18n, { EN, ZH, ZH_HK } from '@/locales';
import { computed, ref } from 'vue';
import SvgIcons from '@/components/SvgIcons.vue';
import { useCommonData } from '@/hooks';

const { enabledLangList, changeLocaleLang } = useLanguageSwitch();

const languageSwitchRef = ref(null);

// 语言标签映射
const langLabelMap = {
	[ZH]: '简',
	[ZH_HK]: '繁',
	[EN]: 'Eng'
};
const sortRule = [ZH, ZH_HK, EN];

const currentLanguage = computed(() => i18n.locale);

// 当前语言标签
const currentLanguageLabel = computed(() => {
	return langLabelMap[currentLanguage.value] || langLabelMap[ZH];
});

// 可用语言列表
const availableLanguages = computed(() => {
	const enabledLanguages = enabledLangList.value;
	return enabledLanguages
		.sort((a, b) => sortRule.indexOf(a) - sortRule.indexOf(b))
		.map(key => ({
			key,
			value: langLabelMap[key]
		}));
});

// 下拉列表语言（排除当前语言）
const dropdownLanguages = computed(() => {
	return availableLanguages.value.filter(lang => lang.key !== currentLanguage.value);
});

const { viewInIframe } = useCommonData();
const handleLangChange = lang => {
	if (viewInIframe.value) return;
	changeLocaleLang(lang);
};
</script>

<style lang="less">
.nav-language-popover {
	&.el-popover {
		padding: 0;
		min-width: 100px;
		border-radius: 2px;
		border: 0.5px solid rgba(var(--surf-primary-color-rgb), 0.3);
		background: var(--surf-secondary-color);
		box-shadow: 0 10px 20px 0 rgba(var(--surf-primary-color-rgb), 0.4);
	}

	&.el-popper[x-placement^='bottom'] {
		margin-top: 12px;
	}

	.nav-language-dropdown {
		padding: 4px;

		.nav-language-dropdown-item {
			position: relative;
			cursor: pointer;

			.nav-language-dropdown-item-content {
				padding: 4px 12px;
				color: rgba(var(--surf-inverse-color-rgb), 0.6);
				font-size: 16px;
				font-weight: 400;
				line-height: 24px;
				display: flex;
				align-items: center;
				justify-content: flex-start;

				&:hover {
					background: rgba(var(--surf-inverse-color-rgb), 0.1);
					color: var(--surf-inverse-color);
					border-radius: 2px;
				}
			}
		}
	}
}
</style>
<style lang="less" scoped>
.nav-language-switch {
	margin-left: 50px;

	.nav-language-switch-trigger {
		border-radius: 100px;
		border: 1px solid rgba(var(--surf-inverse-color-rgb), 0.2);
		padding: 5px 12px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		cursor: pointer;
		transition: all 0.3s;

		&:hover {
			background: rgba(var(--surf-inverse-color-rgb), 0.05);
		}
	}

	.nav-language-switch-item {
		color: var(--surf-inverse-color);
		font-size: 14px;
		font-weight: 400;
		line-height: 18px;
	}

	.nav-language-switch-arrow {
		font-size: 16px;
		color: var(--surf-inverse-color) !important;
		transition: transform 0.3s ease;
	}

	&:hover .nav-language-switch-arrow {
		transform: rotate(180deg);
	}
}
</style>
